import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, RotateCcw, FileCode2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { adminGetDocuments, adminProcessInvoicing, adminRetryDocument, adminGetDocumentXml, formatApiError } from "@/lib/api";

export const DOC_STATUS_UI = {
  to_generate: ["Da generare", "text-slate-300"], generated: ["Generata", "text-cyan-300"], to_send: ["Da inviare", "text-amber-300"],
  sent: ["Inviata", "text-emerald-300"], delivered: ["Consegnata", "text-emerald-300"], rejected: ["Scartata", "text-rose-300"], error: ["Errore", "text-rose-300"],
};
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("it-IT") : "—");

function DocRow({ d, onRetry, onXml }) {
  const [label, cls] = DOC_STATUS_UI[d.status] || [d.status, "text-slate-400"];
  return (
    <div className="py-2.5 flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between text-sm" data-testid={`admin-doc-${d.id}`}>
      <div className="min-w-0">
        <div className="text-slate-200 truncate">{d.number ? `Fattura ${d.number}` : "Numero da assegnare"} · {d.label}</div>
        <div className="text-[11px] text-slate-500 truncate">{fmtDate(d.document_date || d.created_at)} · {d.account_email || d.account_id} · pagamento Stripe <span className="font-mono">{d.payment_id}</span></div>
        {d.config_required && d.status === "to_generate" && <div className="text-[11px] text-amber-300" data-testid={`admin-doc-${d.id}-config`}>Configurazione fatturazione necessaria</div>}
        {d.errors?.length > 0 && <div className="text-[11px] text-rose-300 truncate">{d.errors[d.errors.length - 1].error}</div>}
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <div className="text-right">
          <div className="text-slate-100">€ {Number(d.amount_total ?? d.amount_net).toFixed(2)}{d.amount_total == null ? " + IVA" : ""}</div>
          <div className={`text-[11px] ${cls}`} data-testid={`admin-doc-${d.id}-status`}>{label}{d.sdi_status && d.status !== "sent" ? ` · SDI: ${d.sdi_status}` : ""}</div>
        </div>
        {d.status !== "to_generate" && <Button size="icon" variant="ghost" onClick={() => onXml(d)} title="Scarica XML" data-testid={`admin-doc-${d.id}-xml`}><FileCode2 size={14} /></Button>}
        {(d.status === "error" || d.status === "rejected") && <Button size="icon" variant="ghost" onClick={() => onRetry(d)} title="Nuovo tentativo" data-testid={`admin-doc-${d.id}-retry`}><RotateCcw size={14} /></Button>}
      </div>
    </div>
  );
}

export function InvoiceQueue() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => adminGetDocuments().then(setData).catch(() => setData({ items: [], counts: {} }));
  useEffect(() => { load(); }, []);
  const process = async () => {
    setBusy(true);
    try { const r = await adminProcessInvoicing(); toast.success(`Coda elaborata: ${r.generated} generate, ${r.sent} inviate, ${r.errors} errori, ${r.waiting} in attesa di configurazione`); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const retry = async (d) => { try { await adminRetryDocument(d.id); toast.success("Documento rimesso in coda"); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const xml = async (d) => {
    try {
      const r = await adminGetDocumentXml(d.id);
      const a = document.createElement("a");
      a.href = `data:application/xml;base64,${r.xml_b64}`; a.download = `fattura-${(r.number || d.id).replace("/", "-")}.xml`; a.click();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!data) return <div className="text-sm text-slate-500">Caricamento…</div>;
  return (
    <div className="nadia-card p-5 space-y-3" data-testid="admin-invoice-queue">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="font-heading text-base font-semibold text-slate-100">Fatture elettroniche</div>
        <Button size="sm" variant="outline" onClick={process} disabled={busy} className="border-white/10" data-testid="admin-invoicing-process">
          {busy ? <Loader2 size={13} className="animate-spin mr-1" /> : <RefreshCw size={13} className="mr-1" />} Elabora coda ora
        </Button>
      </div>
      <div className="flex flex-wrap gap-2 text-[11px]" data-testid="admin-invoice-counts">
        {Object.entries(DOC_STATUS_UI).map(([k, [label, cls]]) => (
          <span key={k} className="px-2 py-0.5 rounded-full border border-white/10 bg-white/[0.02]"><span className={cls}>{label}</span> <span className="text-slate-400">{data.counts?.[k] ?? 0}</span></span>
        ))}
      </div>
      {data.items.length === 0
        ? <div className="text-sm text-slate-400" data-testid="admin-invoices-empty">Nessuna fattura: vengono create solo da pagamenti Stripe confermati dal webhook.</div>
        : <div className="divide-y divide-white/5">{data.items.map((d) => <DocRow key={d.id} d={d} onRetry={retry} onXml={xml} />)}</div>}
    </div>
  );
}
